import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import HomeHeroSection from './home/HomeHeroSection';
import {
  TrendingAcrossSection,
  JoinSocietySection,
  PopularBusinessesSection,
  EventsThisWeekSection,
  ExplorePakistanSection,
  PropertiesNearSection,
  FooterCTASection,
} from './home/HomeSections';
import MainGuestWelcomeDialog from './MainGuestWelcomeDialog';
import SocietyCitySearchModal from './SocietyCitySearchModal';
import { useMainPageSocietyGate } from '../../hooks/useMainPageSocietyGate';
import { authService } from '../../services/api';
import { getSocietyUrl } from '../../utils/urlHelpers';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  clearGuestSocietyPreference,
  setGuestSocietyPreference,
} from '../../utils/guestSocietyPreference';

/**
 * Main landing page: Hero, Trending, Societies, Businesses, Events, Explore (societies), Properties, Footer CTA.
 * Authenticated users with a society can open it from the home banner; others see welcome / select-society prompts.
 */
const MainPage = ({ onNavigate }) => {
  const navigate = useNavigate();
  const { showError } = useToast();
  const { user } = useAuth();
  const {
    showGuestWelcome,
    showMissingSocietyPrompt,
    fetchUserData,
    hasSociety,
    guestSocietyPreference,
    hasGuestSocietyPreference,
    isAuthenticated,
    userSociety,
  } = useMainPageSocietyGate();
  const [missingSocietyModalOpen, setMissingSocietyModalOpen] = useState(false);
  const [guestSocietyModalOpen, setGuestSocietyModalOpen] = useState(false);
  const [savingSociety, setSavingSociety] = useState(false);

  const handleGoToMySociety = () => {
    if (!userSociety) return;
    navigate(getSocietyUrl(userSociety));
  };

  const handleSaveProfileSociety = async (society) => {
    setSavingSociety(true);
    try {
      await authService.updateUserProfile({ society: society.id });
      await fetchUserData();
      setMissingSocietyModalOpen(false);
      navigate(getSocietyUrl(society), { replace: true });
    } catch (err) {
      showError(err?.message || 'Could not save your society. Please try again.');
    } finally {
      setSavingSociety(false);
    }
  };

  const handleContinueWithLastSociety = () => {
    if (!guestSocietyPreference) return;
    navigate(getSocietyUrl(guestSocietyPreference));
  };

  const handleGuestSocietyPicked = (society) => {
    setGuestSocietyPreference(society);
    setGuestSocietyModalOpen(false);
    navigate(getSocietyUrl(society));
  };

  const handleResetGuestSocietyPreference = () => {
    clearGuestSocietyPreference();
    setGuestSocietyModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header onNavigate={onNavigate} />

      {isAuthenticated && hasSociety && userSociety && (
        <div
          className="bg-emerald-50 border-b border-emerald-200 px-4 py-3"
          role="region"
          aria-label="Your society"
        >
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-emerald-900">
              Your society is{' '}
              <span className="font-semibold">
                {userSociety.name || userSociety.slug || 'your community'}
              </span>
              . Open it when you want local posts, businesses, and events.
            </p>
            <button
              type="button"
              onClick={handleGoToMySociety}
              className="shrink-0 px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
            >
              Go to my society
            </button>
          </div>
        </div>
      )}

      {showMissingSocietyPrompt && (
        <div
          className="bg-amber-50 border-b border-amber-200 px-4 py-3"
          role="region"
          aria-label="Society required"
        >
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-amber-900">
              Please select your society to personalize your experience and access your community.
            </p>
            <button
              type="button"
              onClick={() => setMissingSocietyModalOpen(true)}
              className="shrink-0 px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
            >
              Select society
            </button>
          </div>
        </div>
      )}

      {!isAuthenticated && hasGuestSocietyPreference && !showGuestWelcome && (
        <div
          className="bg-emerald-50 border-b border-emerald-200 px-4 py-3"
          role="region"
          aria-label="Continue with your selected society"
        >
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-emerald-900">
              Continue with{' '}
              <span className="font-semibold">
                {guestSocietyPreference?.name || guestSocietyPreference?.slug || 'your society'}
              </span>
              .
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleContinueWithLastSociety}
                className="shrink-0 px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
              >
                Continue
              </button>
              <button
                type="button"
                onClick={handleResetGuestSocietyPreference}
                className="shrink-0 px-4 py-2 rounded-xl border border-emerald-300 text-emerald-800 text-sm font-medium hover:bg-emerald-100"
              >
                Change
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="flex-grow pb-16 md:pb-0">
        <HomeHeroSection />
        <TrendingAcrossSection />
        <JoinSocietySection />
        <PopularBusinessesSection />
        <EventsThisWeekSection />
        <ExplorePakistanSection />
        <PropertiesNearSection />
        <FooterCTASection />
      </main>

      <Footer onNavigate={onNavigate} />

      {showGuestWelcome && <MainGuestWelcomeDialog open />}

      <SocietyCitySearchModal
        open={missingSocietyModalOpen}
        onClose={() => !savingSociety && setMissingSocietyModalOpen(false)}
        title="Select your society"
        saving={savingSociety}
        requesterEmail={user?.email || ''}
        onConfirm={handleSaveProfileSociety}
      />

      <SocietyCitySearchModal
        open={guestSocietyModalOpen}
        onClose={() => setGuestSocietyModalOpen(false)}
        title="Select your society"
        onConfirm={handleGuestSocietyPicked}
      />
    </div>
  );
};

export default MainPage;
